import React from "react";
import _ from "lodash";
import { Link, useLocation, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { Nav, NavLink, NavItem } from "reactstrap";
import {
  BsFillCameraVideoFill,
  BsFilm,
  BsQuestionCircle,
  BsSearch,
  BsPersonSquare
} from "react-icons/bs";
import { ImSphere } from "react-icons/im";
import clientPropType from "../../../prop-types/client";
import TabFilters from "./tab-filters";
import Tooltip from "../../../commons/tooltip/components/tooltip";
import AccountNavigation from "./account-navigation";
import {
  getSearchIconClassName,
  getTabClassName,
  getPersonIconClassName,
  getTabFiltersClassName,
  getAccountNavigationClassName
} from "../../../utils/style-helper";
import {
  ClientType,
  ON_DEMAND,
  SCHEDULED,
  NETWORK,
  USER,
  LOGIN,
  EVENTS,
  FAQ
} from "../../../constants";

const propTypes = {
  client: clientPropType.isRequired,
  user: PropTypes.object.isRequired,
  filters: PropTypes.shape({
    conferenceId: PropTypes.number,
    clientId: PropTypes.number,
    sport: PropTypes.number,
    category: PropTypes.number
  }).isRequired,
  activeTab: PropTypes.string.isRequired,
  typeOptions: PropTypes.array.isRequired,
  isFiltersMenuOpen: PropTypes.bool.isRequired,
  isAccountMenuOpen: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  logout: PropTypes.func.isRequired,
  toggleFiltersNav: PropTypes.func.isRequired,
  toggleAccountNav: PropTypes.func.isRequired,
  toggleSideNavs: PropTypes.func.isRequired,
  onSchoolFilter: PropTypes.func.isRequired,
  onConferenceFilter: PropTypes.func.isRequired,
  onEventTypeFilter: PropTypes.func.isRequired,
  selectedSportCallback: PropTypes.func.isRequired,
  selectedCategoryCallback: PropTypes.func.isRequired
};

function NavigationLinks(props) {
  const {
    client: {
      id,
      clientType,
      siblings,
      features,
      children,
      grandchildren,
      parentId,
      branding: { relationshipLogo },
      parent,
      grandparent,
      categories
    },
    user,
    filters,
    activeTab,
    typeOptions,
    isFiltersMenuOpen,
    isAccountMenuOpen,
    isLoggedIn,
    logout,
    toggleFiltersNav,
    toggleAccountNav,
    toggleSideNavs,
    onSchoolFilter,
    onConferenceFilter,
    onEventTypeFilter,
    selectedSportCallback,
    selectedCategoryCallback
  } = props;

  const location = useLocation();
  const params = useParams();

  const isOrg = clientType.toLowerCase() === ClientType.ORGANIZATION;
  const isConference = clientType.toLowerCase() === ClientType.CONFERENCE;
  let redirectRelationship;
  let schools = [...siblings];
  if (isOrg) {
    schools = [...grandchildren];
    // We had a conference selected
    if (filters.conferenceId !== null) {
      // And so we filter schools with that parentId
      schools = schools.filter(
        (c) => c.parentClientId && c.parentClientId === filters.conferenceId
      );
    }
  } else if (isConference) {
    schools = [...children];
  }

  if (grandparent && Array.isArray(grandparent) && !_.isEmpty(grandparent)) {
    redirectRelationship = `${window.location.protocol}//${grandparent[0].redirectUrl}`;
  } else if (parent && Array.isArray(parent) && !_.isEmpty(parent)) {
    redirectRelationship = `${window.location.protocol}//${parent[0].redirectUrl}`;
  }

  const hideSchoolFilter =
    !parentId && clientType.toLowerCase() === ClientType.CLIENT;

  const userIsLogged = (user && user.id) || isLoggedIn;

  const isAccountNavOpen = isAccountMenuOpen && userIsLogged;

  return (
    <>
      <Nav className="w-100" tabs>
        <NavItem className="ml-0">
          <span
            className={getSearchIconClassName(isFiltersMenuOpen)}
            onClick={toggleFiltersNav}
            id="search-icon"
          >
            <BsSearch className="main-nav-bar-svg" />
          </span>
          <Tooltip
            className="search-tooltip"
            text="Search"
            target="search-icon"
          />
        </NavItem>
        <NavItem>
          <Link
            className={getTabClassName(activeTab, SCHEDULED)}
            onClick={toggleSideNavs}
            to={`/${params.shortName}/${EVENTS}/${SCHEDULED}${location.search}`}
            id="scheduled-icon"
          >
            <BsFillCameraVideoFill className="main-nav-bar-svg" />
            <span className="pl-2">Scheduled</span>
          </Link>
          <Tooltip text="Scheduled" target="scheduled-icon" />
        </NavItem>
        {features.ondemand && (
          <NavItem id="on-demand-icon">
            <Link
              className={getTabClassName(activeTab, ON_DEMAND)}
              onClick={toggleSideNavs}
              to={`/${params.shortName}/${EVENTS}/${ON_DEMAND}${location.search}`}
            >
              <BsFilm className="main-nav-bar-svg" />
              <span className="pl-2">On Demand</span>
            </Link>
            <Tooltip text="On Demand" target="on-demand-icon" />
          </NavItem>
        )}
        {features.network && (
          <NavItem>
            <Link
              className={getTabClassName(activeTab, NETWORK)}
              onClick={toggleSideNavs}
              to={`/${params.shortName}/${NETWORK}${location.search}`}
              id="network-icon"
            >
              <ImSphere className="main-nav-bar-svg network-icon" />
              <span className="pl-2">Network</span>
            </Link>
            <Tooltip text="Network" target="network-icon" />
          </NavItem>
        )}
        <NavItem>
          <NavLink id="help-icon" href={FAQ} target="_blank">
            <span className="d-none">Help</span>
            <BsQuestionCircle className="main-nav-bar-svg" size="25px" />
          </NavLink>
          <Tooltip className="help-tooltip" text="Help" target="help-icon" />
        </NavItem>
        <Nav className="ml-auto">
          {relationshipLogo && redirectRelationship && (
            <NavItem>
              <NavLink href={redirectRelationship} target="_blank" disabled>
                <span className="d-none">Org/Conference</span>
                <img
                  src={`${window.location.protocol}//${relationshipLogo}`}
                  alt=""
                  style={{ width: 25, height: 25 }}
                />
              </NavLink>
            </NavItem>
          )}
          <NavItem>
            {!userIsLogged ? (
              <>
                <Link
                  className={getTabClassName(activeTab, USER)}
                  onClick={toggleSideNavs}
                  to={`/${params.shortName}/${LOGIN}${location.search}`}
                  id="login-icon"
                >
                  <BsPersonSquare className="main-nav-bar-svg" />
                </Link>
                <Tooltip text="Sign In" target="login-icon" />
              </>
            ) : (
              <span
                className={`${getPersonIconClassName(isAccountNavOpen)}`}
                onClick={toggleAccountNav}
                id="account-tab"
              >
                <BsPersonSquare className="main-nav-bar-svg" />
                <Tooltip text="My Account" target="account-tab" />
              </span>
            )}
          </NavItem>
        </Nav>
      </Nav>
      <>
        <div className={getTabFiltersClassName(isFiltersMenuOpen)}>
          <TabFilters
            isExpanded={isFiltersMenuOpen}
            activeTab={activeTab}
            schools={schools}
            filters={filters}
            conferences={children}
            onSelectConference={onConferenceFilter}
            onSelectSchool={onSchoolFilter}
            onSelectSport={selectedSportCallback}
            onEventTypeFilter={onEventTypeFilter}
            onSelectCategory={selectedCategoryCallback}
            id={id}
            conferenceId={
              !_.isNull(filters.conferenceId) ? filters.conferenceId : id
            }
            sportTypes={typeOptions}
            isOrg={isOrg}
            hideSchoolFilter={hideSchoolFilter}
            showSportsFilter={features.sports}
            showMembersFilter={features.members}
            showConferenceFilter={features.conference}
            categories={categories}
            showCategoryFilter={features.category}
          />
        </div>
        {isFiltersMenuOpen && <div className="navigation-overlay" />}
      </>
      <>
        <div className={getAccountNavigationClassName(isAccountNavOpen)}>
          <AccountNavigation
            user={user}
            isOpen={!!isAccountNavOpen}
            logout={logout}
          />
        </div>
        {isAccountNavOpen && <div className="navigation-overlay" />}
      </>
    </>
  );
}

NavigationLinks.propTypes = propTypes;

export default NavigationLinks;
